import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { Search, Mail, Phone, Award, FileText, FileSpreadsheet, Pencil, Trash2, AlertTriangle, X } from "lucide-react";
import { toast, Toaster } from "sonner";
import { downloadFile } from "../../lib/download";

export default function AdminCustomers() {
    const { user: me } = useAuth();
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    // Iter 38 — pagination: 20 per page, S.No. with absolute positioning
    const PAGE_SIZE = 20;
    const [page, setPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE));
    const pageUsers = users.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    // S.No. starts at 1 for filtered/all results on page 1; page 2 starts at 21 etc.

    // Edit modal
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", loyalty_points: 0 });
    const [saving, setSaving] = useState(false);

    // Delete + Clear All confirmation dialogs
    const [confirm, setConfirm] = useState(null);
    // shape: { type: 'one' | 'all', user?, text }

    const fetch = async () => {
        setLoading(true);
        try {
            const params = q ? { q } : {};
            const { data } = await api.get("/admin/customers", { params });
            setUsers(data);
            setPage(1);          // Iter 38 — reset to page 1 on every fetch (incl. search)
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { fetch(); /* eslint-disable-next-line */ }, []);

    const openEdit = (u) => {
        setEditing(u);
        setForm({
            name: u.name || "",
            email: u.email || "",
            phone: u.phone || "",
            loyalty_points: u.loyalty_points || 0,
        });
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        if (!form.name) { toast.error("Name is required"); return; }
        if (!form.email) { toast.error("Email is required"); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name, email: form.email,
                phone: form.phone || null,
                loyalty_points: parseInt(form.loyalty_points) || 0,
            };
            const { data } = await api.put(`/admin/customers/${editing.id}`, payload);
            setUsers(prev => prev.map(u => u.id === data.id ? data : u));
            toast.success("Customer updated");
            setEditing(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    const confirmAndRun = async () => {
        if (!confirm) return;
        try {
            if (confirm.type === "one") {
                await api.delete(`/admin/customers/${confirm.user.id}`);
                setUsers(prev => prev.filter(u => u.id !== confirm.user.id));
                toast.success(`Deleted ${confirm.user.name}`);
            } else if (confirm.type === "all") {
                const { data } = await api.post(`/admin/customers/clear-all`);
                setUsers([]);
                toast.success(`Cleared ${data.deleted_count} customer${data.deleted_count === 1 ? "" : "s"}`);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Operation failed");
        } finally {
            setConfirm(null);
        }
    };

    const isSuper = me?.role === "super_admin";

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-display text-3xl font-bold uppercase text-white">Customers</h1>
                        <p className="text-slate-400 text-sm mt-1">All registered players.</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => downloadFile("/admin/reports/customers.pdf", "customers.pdf", q ? { q } : {})}
                            data-testid="customers-pdf-btn"
                            className="inline-flex items-center gap-2 px-3 h-10 bg-slate-200 text-slate-900 font-bold uppercase tracking-widest text-xs rounded hover:bg-white">
                            <FileText className="w-4 h-4" /> PDF
                        </button>
                        <button onClick={() => downloadFile("/admin/reports/customers.xlsx", "customers.xlsx", q ? { q } : {})}
                            data-testid="customers-excel-btn"
                            className="inline-flex items-center gap-2 px-3 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52]">
                            <FileSpreadsheet className="w-4 h-4" /> Excel
                        </button>
                        {isSuper && (
                            <button onClick={() => setConfirm({ type: "all" })} disabled={users.length === 0}
                                data-testid="customers-clear-all-btn" title="Bulk delete all customers (super-admin)"
                                className="inline-flex items-center gap-2 px-3 h-10 bg-red-600/15 border border-red-500/40 text-red-300 hover:bg-red-600/25 hover:text-red-200 font-bold uppercase tracking-widest text-xs rounded disabled:opacity-40 disabled:cursor-not-allowed">
                                <Trash2 className="w-4 h-4" /> Clear All
                            </button>
                        )}
                    </div>
                </div>

                <div className="mt-6 relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetch()}
                        placeholder="Search by name, email, phone..." data-testid="customer-search-input"
                        className="w-full h-10 pl-10 pr-4 bg-slate-900 border border-slate-800 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                </div>

                <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-slate-800 bg-slate-950/50">
                                    <th className="text-left py-3 px-4 w-16">S.No.</th>
                                    <th className="text-left py-3 px-4">Name</th>
                                    <th className="text-left py-3 px-4">Email</th>
                                    <th className="text-left py-3 px-4">Phone</th>
                                    <th className="text-right py-3 px-4">Bookings</th>
                                    <th className="text-right py-3 px-4">Loyalty</th>
                                    <th className="text-right py-3 px-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="text-slate-300">
                                {loading ? <tr><td colSpan={7} className="py-8 text-center text-slate-500">Loading...</td></tr> :
                                users.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-slate-500">No customers.</td></tr> :
                                pageUsers.map((u, idx) => (
                                    <tr key={u.id} className="border-b border-slate-900 hover:bg-slate-800/30" data-testid={`customer-${u.id}`}>
                                        <td className="py-3 px-4 text-slate-500 font-mono text-xs" data-testid={`customer-sno-${u.id}`}>{(page - 1) * PAGE_SIZE + idx + 1}</td>
                                        <td className="py-3 px-4 text-white">{u.name}</td>
                                        <td className="py-3 px-4"><span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</span></td>
                                        <td className="py-3 px-4">{u.phone ? <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone}</span> : <span className="text-slate-600">—</span>}</td>
                                        <td className="py-3 px-4 text-right font-bold text-white">{u.bookings_count}</td>
                                        <td className="py-3 px-4 text-right font-bold text-[#00FF66]"><span className="inline-flex items-center gap-1"><Award className="w-3 h-3" />{u.loyalty_points}</span></td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => openEdit(u)} title="Edit"
                                                    data-testid={`edit-customer-${u.id}`}
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded text-slate-400 hover:text-white hover:bg-slate-800">
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => setConfirm({ type: "one", user: u })} title="Delete"
                                                    data-testid={`delete-customer-${u.id}`}
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded text-slate-400 hover:text-red-400 hover:bg-slate-800">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Iter 38 — Pagination footer (20/page, S.No. resets on search via fetch()) */}
                    {!loading && users.length > 0 && (
                        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-800 text-xs uppercase tracking-widest text-slate-400" data-testid="customers-pagination">
                            <div>
                                Showing <span className="text-white font-bold">{(page - 1) * PAGE_SIZE + 1}</span>–<span className="text-white font-bold">{Math.min(page * PAGE_SIZE, users.length)}</span> of <span className="text-white font-bold">{users.length}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                    data-testid="customers-prev-page"
                                    className="px-3 h-8 rounded border border-slate-700 text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800">Prev</button>
                                <span className="text-slate-400">Page <span className="text-white font-bold">{page}</span> / {totalPages}</span>
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                    data-testid="customers-next-page"
                                    className="px-3 h-8 rounded border border-slate-700 text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-800">Next</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Edit modal */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <form onSubmit={saveEdit} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-4" data-testid="edit-customer-form">
                        <div className="flex items-center justify-between">
                            <h3 className="font-display text-2xl font-bold uppercase text-white">Edit Customer</h3>
                            <button type="button" onClick={() => setEditing(null)} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <Field label="Name" value={form.name} onChange={v => setForm({ ...form, name: v })} testid="edit-cust-name" required />
                        <Field label="Email" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} testid="edit-cust-email" required />
                        <Field label="Phone (+91…)" value={form.phone} onChange={v => setForm({ ...form, phone: v })} testid="edit-cust-phone" placeholder="+919999999999" />
                        <Field label="Loyalty Points" type="number" value={form.loyalty_points} onChange={v => setForm({ ...form, loyalty_points: v })} testid="edit-cust-loyalty" />
                        <div className="flex gap-2 pt-2">
                            <button type="button" onClick={() => setEditing(null)} className="flex-1 h-11 bg-slate-800 border border-slate-700 text-white font-bold uppercase tracking-widest rounded hover:bg-slate-700">Cancel</button>
                            <button type="submit" disabled={saving} data-testid="save-customer-btn"
                                className="flex-1 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] disabled:opacity-50">
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Confirm dialog (single delete + clear-all) */}
            {confirm && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="w-full max-w-sm bg-slate-900 border border-red-500/40 rounded-lg p-6 space-y-4" data-testid="confirm-dialog">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/40 flex items-center justify-center flex-shrink-0">
                                <AlertTriangle className="w-5 h-5 text-red-400" />
                            </div>
                            <div>
                                <h3 className="font-display text-lg font-bold uppercase text-white">
                                    {confirm.type === "all" ? "Delete ALL customers?" : "Delete this customer?"}
                                </h3>
                                <p className="text-sm text-slate-400 mt-1">
                                    {confirm.type === "all"
                                        ? `This will permanently remove ALL ${users.length} player accounts. Their bookings will remain in reports but anonymised. This action cannot be undone.`
                                        : <>This will permanently remove <b className="text-white">{confirm.user.name}</b> ({confirm.user.email}). Their booking history stays in reports but anonymised. This cannot be undone.</>
                                    }
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setConfirm(null)} data-testid="confirm-cancel-btn"
                                className="flex-1 h-11 bg-slate-800 border border-slate-700 text-white font-bold uppercase tracking-widest rounded hover:bg-slate-700">
                                Cancel
                            </button>
                            <button onClick={confirmAndRun} data-testid="confirm-delete-btn"
                                className="flex-1 h-11 bg-red-600 text-white font-bold uppercase tracking-widest rounded hover:bg-red-500">
                                {confirm.type === "all" ? "Yes, Delete All" : "Yes, Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

function Field({ label, type = "text", value, onChange, testid, required, placeholder }) {
    return (
        <div>
            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">{label}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
                required={required} data-testid={testid}
                className="w-full mt-2 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
        </div>
    );
}
